import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import ActionMenu from '../components/common/ActionMenu';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/** Leadership roles read gold, officers navy, members neutral. */
const ROLE_BADGE = (role) => {
  if (['Chairman', 'Vice Chairman', 'Patron'].includes(role)) return 'badge-gold';
  if (['Secretary', 'Treasurer', 'Organizing Secretary', 'Advisor'].includes(role)) {
    return 'badge-navy';
  }
  return 'badge-neutral';
};

const initials = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const AdminAlumni = () => {
  const { user } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    department_id: '',
    graduation_year: '',
    employment_status: '',
    employer: '',
    journey_description: '',
    email: '',
    phone: '',
    display_order: 0,
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [searchTerm, setSearchTerm] = useState('');

  const canManage = user?.role === 'admin' || user?.role === 'editor';
  const roles = ['Chairman', 'Vice Chairman', 'Treasurer', 'Secretary', 'Member', 'Patron', 'Advisor'];

  const fetchAlumni = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni');
      console.log('Fetched alumni:', response.data);
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
      toast.error('Failed to fetch alumni');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    fetchAlumni();
    fetchDepartments();
  }, [fetchAlumni]);

  // Refresh when refreshKey changes
  useEffect(() => {
    fetchAlumni();
  }, [refreshKey, fetchAlumni]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File too large! Maximum size is 5MB');
        return;
      }
      setFormData({ ...formData, photo: file });
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      toast.error('Name and role are required');
      return;
    }
    
    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('role', formData.role);
    submitData.append('department_id', formData.department_id);
    submitData.append('graduation_year', formData.graduation_year);
    submitData.append('employment_status', formData.employment_status);
    submitData.append('employer', formData.employer);
    submitData.append('journey_description', formData.journey_description);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('display_order', formData.display_order);
    if (formData.photo) {
      submitData.append('photo', formData.photo);
    }
    
    setLoading(true);
    try {
      if (editingAlumni) {
        await api.put(`/alumni/${editingAlumni.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Alumni updated successfully');
      } else {
        await api.post('/alumni', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Alumni added successfully');
      }
      
      // Reset form
      resetForm();
      
      // Force refresh by updating refreshKey
      setRefreshKey(Date.now());
      
    } catch (error) {
      console.error('Error saving alumni:', error);
      toast.error(error.response?.data?.message || 'Failed to save alumni');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (alumnus) => {
    setEditingAlumni(alumnus);
    setFormData({
      name: alumnus.name,
      role: alumnus.role,
      department_id: alumnus.department_id || '',
      graduation_year: alumnus.graduation_year || '',
      employment_status: alumnus.employment_status || '',
      employer: alumnus.employer || '',
      journey_description: alumnus.journey_description || '',
      email: alumnus.email || '',
      phone: alumnus.phone || '',
      display_order: alumnus.display_order || 0,
      photo: null
    });
    if (alumnus.photo_url) {
      setPhotoPreview(`${alumnus.photo_url}?t=${refreshKey}`);
    } else {
      setPhotoPreview(null);
    }
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this alumni?')) return;
    
    try {
      await api.delete(`/alumni/${id}`);
      toast.success('Alumni deleted successfully');
      setRefreshKey(Date.now());
    } catch (error) {
      console.error('Error deleting alumni:', error);
      toast.error('Failed to delete alumni');
    }
  };

  const handleView = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAlumni(null);
    setFormData({
      name: '',
      role: '',
      department_id: '',
      graduation_year: '',
      employment_status: '',
      employer: '',
      journey_description: '',
      email: '',
      phone: '',
      display_order: 0,
      photo: null
    });
    setPhotoPreview(null);
  };

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    // Add timestamp to force refresh
    return `${photoUrl}?v=${refreshKey}`;
  };

  const getRoleBadgeClass = (role) => ROLE_BADGE(role);

  const filteredAlumni = alumni.filter((alumnus) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return [alumnus.name, alumnus.role, alumnus.department_name, alumnus.graduation_year]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  return (
    <div>
      {/* Toolbar — the page title and context live in the admin header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-500">
            <span className="font-semibold text-ink-900">{alumni.length}</span>{' '}
            {alumni.length === 1 ? 'member' : 'members'}
            {searchTerm && filteredAlumni.length !== alumni.length && (
              <span className="text-ink-400"> · {filteredAlumni.length} shown</span>
            )}
          </p>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, role or department"
              aria-label="Search alumni"
              className="input-field w-64 py-2 pl-9 text-sm"
            />
          </div>
        </div>

        {canManage && (
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {showForm ? 'Close form' : 'Add alumni'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingAlumni ? 'Edit Alumni' : 'Add New Alumni'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., John Doe"
                required
              />
              
              <div>
                <label className="label">Role *</label>
                <select
                  className="input-field"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Department</label>
                <select
                  className="input-field"
                  value={formData.department_id}
                  onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <Input
                label="Graduation Year"
                type="number"
                value={formData.graduation_year}
                onChange={(e) => setFormData({...formData, graduation_year: e.target.value})}
                placeholder="e.g., 2024"
              />

              <Input
                label="Employment Status"
                value={formData.employment_status}
                onChange={(e) => setFormData({...formData, employment_status: e.target.value})}
                placeholder="e.g., Employed, Self-Employed, Student"
              />

              <Input
                label="Employer / Organization"
                value={formData.employer}
                onChange={(e) => setFormData({...formData, employer: e.target.value})}
                placeholder="e.g., Company Name"
              />

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@example.com"
              />

              <Input
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="e.g., 0712345678"
              />

              <Input
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                placeholder="Lower numbers appear first"
              />

              <div className="md:col-span-2">
                <label className="label">Journey Description</label>
                <textarea
                  className="input-field"
                  rows="6"
                  value={formData.journey_description}
                  onChange={(e) => setFormData({...formData, journey_description: e.target.value})}
                  placeholder="Write a detailed description of their journey after graduation..."
                  style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="label">Photo (Max 5MB)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {photoPreview && (
                  <div className="mt-2">
                    <img src={photoPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingAlumni ? 'Update' : 'Save')}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Alumni table */}
      <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-ink-200 bg-ink-50">
              <tr>
                {['Photo', 'Name', 'Role', 'Department', 'Grad year', 'Order', 'Actions'].map(
                  (label) => (
                    <th
                      key={label}
                      scope="col"
                      className="whitespace-nowrap px-4 py-3 text-left text-2xs font-bold uppercase tracking-wider text-ink-500"
                    >
                      {label}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {filteredAlumni.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-ink-700">
                      {alumni.length === 0 ? 'No alumni found' : 'No members match your search'}
                    </p>
                    <p className="mt-1 text-xs text-ink-500">
                      {alumni.length === 0
                        ? 'Add the first committee member to get started.'
                        : 'Try a different name, role or department.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredAlumni.map((alumnus) => (
                  <tr key={alumnus.id} className="transition-colors hover:bg-brand-50/40">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleView(alumnus)}
                        aria-label={`View ${alumnus.name}`}
                        className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-brand-800 text-xs font-bold text-white/80"
                      >
                        <span aria-hidden="true">{initials(alumnus.name)}</span>
                        {alumnus.photo_url ? (
                          <img
                            src={getImageUrl(alumnus.photo_url)}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-ink-900">
                      {alumnus.name}
                    </td>
                    <td className="px-4 py-3">
                      <span className={getRoleBadgeClass(alumnus.role)}>{alumnus.role}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-600">
                      {alumnus.department_name || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-ink-600">
                      {alumnus.graduation_year || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-ink-500">
                      {alumnus.display_order}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <ActionMenu
                        label={`Actions for ${alumnus.name}`}
                        items={[
                          { label: 'View profile', icon: Eye, onClick: () => handleView(alumnus) },
                          { label: 'Edit', icon: Pencil, onClick: () => handleEdit(alumnus) },
                          {
                            label: 'Delete',
                            icon: Trash2,
                            variant: 'danger',
                            onClick: () => handleDelete(alumnus.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Alumni Modal */}
      {showViewModal && selectedAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Alumni Profile</h2>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                aria-label="Close"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  <div className="w-full h-64 rounded-lg overflow-hidden bg-gray-100">
                    {getImageUrl(selectedAlumni.photo_url) ? (
                      <img
                        src={getImageUrl(selectedAlumni.photo_url)}
                        alt={selectedAlumni.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAlumni.name)}&background=1e3a8a&color=fff&size=200`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">{selectedAlumni.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:w-2/3">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">{selectedAlumni.name}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getRoleBadgeClass(selectedAlumni.role)}`}>
                      {selectedAlumni.role}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {selectedAlumni.department_name && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Department:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.department_name}</span>
                      </div>
                    )}
                    {selectedAlumni.graduation_year && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Graduation Year:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.graduation_year}</span>
                      </div>
                    )}
                    {selectedAlumni.employment_status && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Employment Status:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employment_status}</span>
                      </div>
                    )}
                    {selectedAlumni.employer && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Employer:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employer}</span>
                      </div>
                    )}
                    {selectedAlumni.email && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Email:</span>
                        <span className="text-gray-900 sm:w-2/3">
                          <a href={`mailto:${selectedAlumni.email}`} className="text-primary hover:underline">{selectedAlumni.email}</a>
                        </span>
                      </div>
                    )}
                    {selectedAlumni.phone && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Phone:</span>
                        <span className="text-gray-900 sm:w-2/3">
                          <a href={`tel:${selectedAlumni.phone}`} className="text-primary hover:underline">{selectedAlumni.phone}</a>
                        </span>
                      </div>
                    )}
                  </div>
                  {selectedAlumni.journey_description && (
                    <div className="mt-6 pt-4 border-t">
                      <h3 className="font-semibold text-gray-800 mb-3 text-lg">My Journey</h3>
                      <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selectedAlumni.journey_description}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowViewModal(false)}>Close</Button>
              {canManage && (
                <Button onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedAlumni);
                }}>Edit Profile</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAlumni;
